// src/utils/slug-utils.js
// Slug management utilities for practitioner public website URLs
// Slugs are stored in Firestore: slugs/{slug} → { practitionerId, createdAt }
// Practitioners/{uid} also gets a `slug` field on first publish.

import { db } from '../config/firebase-config';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Validate a slug string.
 * Rules: 3-30 chars, lowercase letters/numbers/hyphens, no leading/trailing/double hyphens.
 * Returns { valid: boolean, error: string|null }
 */
export function validateSlug(slug) {
  if (!slug || typeof slug !== 'string') {
    return { valid: false, error: 'URL handle is required' };
  }
  const trimmed = slug.trim();
  if (trimmed.length < 3) {
    return { valid: false, error: 'Must be at least 3 characters' };
  }
  if (trimmed.length > 30) {
    return { valid: false, error: 'Must be 30 characters or less' };
  }
  if (!/^[a-z0-9-]+$/.test(trimmed)) {
    return { valid: false, error: 'Only lowercase letters, numbers, and hyphens' };
  }
  if (trimmed.startsWith('-') || trimmed.endsWith('-')) {
    return { valid: false, error: 'Cannot start or end with a hyphen' };
  }
  if (trimmed.includes('--')) {
    return { valid: false, error: 'No consecutive hyphens allowed' };
  }
  return { valid: true, error: null };
}

/**
 * Generate a URL slug suggestion from a display name.
 * Returns a clean slug string (may need availability check).
 */
export function generateSlugSuggestion(displayName) {
  if (!displayName) return '';
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')    // remove invalid chars
    .trim()
    .replace(/\s+/g, '-')            // spaces to hyphens
    .replace(/-+/g, '-')             // deduplicate hyphens
    .replace(/^-|-$/g, '')           // trim leading/trailing hyphens
    .substring(0, 30);
}

/**
 * Check if a slug is available in Firestore.
 * Returns true if available, false if taken.
 * Throws on Firestore errors.
 */
export async function isSlugAvailable(slug) {
  try {
    const slugDoc = await getDoc(doc(db, 'slugs', slug));
    return !slugDoc.exists();
  } catch (err) {
    console.error('isSlugAvailable error:', err);
    throw err;
  }
}

/**
 * Claim a slug — writes to:
 *   slugs/{slug} = { practitionerId, createdAt }
 *   practitioners/{uid} slug field
 * Throws on error.
 */
export async function claimSlug(slug, userId) {
  await setDoc(doc(db, 'slugs', slug), {
    practitionerId: userId,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'practitioners', userId), {
    slug,
    slugClaimedAt: serverTimestamp(),
  });
}

/**
 * Change an existing slug to a new one.
 * 1. Claims the new slug in Firestore.
 * 2. Updates the practitioner doc's slug field.
 * 3. Releases the old slug (best-effort — non-critical).
 * Throws if the new slug cannot be claimed.
 */
export async function changeSlug(oldSlug, newSlug, userId) {
  // Claim the new slug first — if this fails the old slug is untouched
  await setDoc(doc(db, 'slugs', newSlug), {
    practitionerId: userId,
    createdAt: serverTimestamp(),
  });
  // Update practitioner doc
  await updateDoc(doc(db, 'practitioners', userId), {
    slug: newSlug,
    slugChangedAt: serverTimestamp(),
  });
  // Release old slug (non-critical — if it fails the old URL just becomes a dead end)
  if (oldSlug) {
    deleteDoc(doc(db, 'slugs', oldSlug)).catch((err) => {
      console.warn('⚠️ Failed to delete old slug (non-critical):', err);
    });
  }
}

/**
 * Look up the practitioner ID associated with a slug.
 * Returns practitionerId string, or null if slug doesn't exist.
 */
export async function lookupSlug(slug) {
  try {
    const slugDoc = await getDoc(doc(db, 'slugs', slug));
    if (!slugDoc.exists()) return null;
    return slugDoc.data().practitionerId;
  } catch (err) {
    console.error('lookupSlug error:', err);
    return null;
  }
}
